import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { API_BASE, getCsrfToken } from "../services/server";
import Main from "./Main";
import { makeCompanyNav } from "./nav";
import "./css/koncern.css";


function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK", maximumFractionDigits: 0 }).format(n);
}

const COMPANY_LINKS = [
  { label: "Dashboard",      path: (orgnr) => `/dashboard/${orgnr}` },
  { label: "Värdering",      path: (orgnr) => `/valuation/${orgnr}` },
  { label: "PM Studio",      path: (orgnr) => `/pm/${orgnr}` },
  { label: "Due diligence",  path: (orgnr) => `/duediligence/${orgnr}` },
  { label: "Datarum",        path: (orgnr) => `/dataroom/${orgnr}` },
  { label: "Logg",           path: (orgnr) => `/logg/${orgnr}` },
];

export default function Koncern() {
  const { company } = useParams();
  const nav = makeCompanyNav(company);

  const [group, setGroup]                   = useState(null);
  const [valuation, setValuation]           = useState(null);
  const [loading, setLoading]               = useState(true);
  const [valuationLoading, setValuationLoading] = useState(false);
  const [error, setError]                   = useState("");

  // "overview" | orgnr string for individual company
  const [selected, setSelected] = useState("overview");

  const [orgnrInput, setOrgnrInput] = useState("");
  const [bulkInput, setBulkInput]   = useState("");
  const [bulkMode, setBulkMode]     = useState(false);
  const [adding, setAdding]         = useState(false);
  const [addError, setAddError]     = useState("");

  const BASE = `${API_BASE}/users/workspaces/${company}/koncern`;

  const loadGroup = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BASE}/`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not load koncern.");
      setGroup(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [BASE]);

  useEffect(() => { loadGroup(); }, [loadGroup]);

  async function addOne(orgnr) {
    const res = await fetch(`${BASE}/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-CSRFToken": getCsrfToken() || "" },
      body: JSON.stringify({ orgnr }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(`${orgnr}: ${data.detail || "Could not add company."}`);
  }

  async function addSubsidiary() {
    const orgnr = orgnrInput.trim().replace(/\D/g, "");
    if (!orgnr) return;
    setAdding(true);
    setAddError("");
    try {
      await addOne(orgnr);
      setOrgnrInput("");
      loadGroup();
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAdding(false);
    }
  }

  async function addBulk() {
    const orgnrs = bulkInput
      .split(/[\n,;]+/)
      .map((s) => s.trim().replace(/\D/g, ""))
      .filter((s) => s.length >= 9);
    if (!orgnrs.length) return;
    setAdding(true);
    setAddError("");
    const errors = [];
    for (const orgnr of orgnrs) {
      try { await addOne(orgnr); }
      catch (err) { errors.push(err.message); }
    }
    setBulkInput("");
    loadGroup();
    if (errors.length) setAddError(errors.join(" | "));
    setAdding(false);
  }

  async function removeSubsidiary(id) {
    try {
      await fetch(`${BASE}/${id}/`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-CSRFToken": getCsrfToken() || "" },
      });
      loadGroup();
    } catch { /* ignore */ }
  }

  async function computeValuation() {
    setValuationLoading(true);
    setValuation(null);
    try {
      const res = await fetch(`${BASE}/valuation/`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not compute valuation.");
      setValuation(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setValuationLoading(false);
    }
  }

  // All companies in order: parent first, then subsidiaries
  const allCompanies = group
    ? [{ ...group.parent, isParent: true }, ...group.subsidiaries.map((s) => ({ ...s, isParent: false }))]
    : [];

  const selectedCompany = selected !== "overview"
    ? allCompanies.find((c) => c.orgnr === selected) || null
    : null;

  return (
    <Main navItems={nav} className="koncern-page">
      <div className="koncern-layout">

        {/* ── Left company list ── */}
        <aside className="koncern-sidebar">
          <div className="koncern-sidebar-head">Koncern</div>

          <button
            className={`koncern-sidebar-item ${selected === "overview" ? "koncern-sidebar-item--active" : ""}`}
            onClick={() => setSelected("overview")}
          >
            Koncernöversikt
          </button>

          {loading && <div className="koncern-sidebar-loading">Laddar…</div>}

          {allCompanies.map((c) => (
            <button
              key={c.orgnr}
              className={`koncern-sidebar-item ${selected === c.orgnr ? "koncern-sidebar-item--active" : ""}`}
              onClick={() => setSelected(c.orgnr)}
            >
              <span className="koncern-sidebar-name">{c.name || c.orgnr}</span>
              {c.isParent && <span className="koncern-sidebar-badge">Moderbolag</span>}
            </button>
          ))}
        </aside>

        {/* ── Right content ── */}
        <div className="koncern-main">
          {error && <div className="koncern-error">{error}</div>}

          {/* ── OVERVIEW ── */}
          {selected === "overview" && (
            <div className="koncern-overview">
              <h1 className="koncern-title">Koncernöversikt</h1>
              <p className="koncern-sub">Konsoliderad vy över alla bolag i koncernen.</p>

              {/* Add subsidiary */}
              <div className="koncern-add-header">
                <span className="koncern-add-label">Lägg till dotterbolag</span>
                <button className="koncern-mode-toggle" onClick={() => { setBulkMode((v) => !v); setAddError(""); }}>
                  {bulkMode ? "Enstaka" : "Flera samtidigt"}
                </button>
              </div>

              {bulkMode ? (
                <div className="koncern-bulk-row">
                  <textarea
                    className="koncern-textarea"
                    placeholder={"Klistra in organisationsnummer, ett per rad\nt.ex.\n5560000000\n5560000001"}
                    value={bulkInput}
                    onChange={(e) => setBulkInput(e.target.value)}
                    rows={4}
                  />
                  <button className="koncern-btn-primary" onClick={addBulk} disabled={adding || !bulkInput.trim()}>
                    {adding ? "Hämtar bolagsdata och värderar…" : "Lägg till alla"}
                  </button>
                </div>
              ) : (
                <div className="koncern-add-row">
                  <input
                    className="koncern-input"
                    type="text"
                    placeholder="Organisationsnummer (10 siffror)"
                    value={orgnrInput}
                    onChange={(e) => setOrgnrInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addSubsidiary()}
                  />
                  <button className="koncern-btn-primary" onClick={addSubsidiary} disabled={adding || !orgnrInput.trim()}>
                    {adding ? "Hämtar och värderar…" : "Lägg till bolag"}
                  </button>
                </div>
              )}
              {addError && <p className="koncern-error" style={{ marginTop: 6 }}>{addError}</p>}

              {/* Consolidated table */}
              {!loading && group && (
                <>
                  <table className="koncern-table">
                    <thead>
                      <tr>
                        <th>Bolag</th>
                        <th>Orgnr</th>
                        <th>Räkenskapsår</th>
                        <th className="koncern-num">Omsättning</th>
                        <th className="koncern-num">EBITDA</th>
                        <th className="koncern-num">Nettoskuld</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="koncern-parent-row">
                        <td>
                          <button className="koncern-table-name-btn" onClick={() => setSelected(group.parent.orgnr)}>
                            {group.parent.name}
                          </button>
                          <span className="koncern-badge">Moderbolag</span>
                        </td>
                        <td>{group.parent.orgnr}</td>
                        <td>{group.parent.fiscal_year || "—"}</td>
                        <td className="koncern-num">{fmt(group.parent.revenue)}</td>
                        <td className="koncern-num">{fmt(group.parent.ebitda)}</td>
                        <td className="koncern-num">{fmt(group.parent.net_debt)}</td>
                        <td></td>
                      </tr>

                      {group.subsidiaries.map((s) => (
                        <tr key={s.id}>
                          <td>
                            <button className="koncern-table-name-btn" onClick={() => setSelected(s.orgnr)}>
                              {s.name}
                            </button>
                          </td>
                          <td>{s.orgnr}</td>
                          <td>{s.fiscal_year || "—"}</td>
                          <td className="koncern-num">{fmt(s.revenue)}</td>
                          <td className="koncern-num">{fmt(s.ebitda)}</td>
                          <td className="koncern-num">{fmt(s.net_debt)}</td>
                          <td>
                            <button className="koncern-remove-btn" onClick={() => removeSubsidiary(s.id)} title="Ta bort">×</button>
                          </td>
                        </tr>
                      ))}

                      {group.subsidiaries.length === 0 && (
                        <tr>
                          <td colSpan={7} className="koncern-empty">Inga dotterbolag tillagda ännu.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div style={{ marginTop: 20 }}>
                    <button
                      className="koncern-btn-primary"
                      onClick={computeValuation}
                      disabled={valuationLoading || group.subsidiaries.length === 0}
                    >
                      {valuationLoading ? "Beräknar…" : "Beräkna koncernvärdering"}
                    </button>
                  </div>
                </>
              )}

              {/* Valuation result */}
              {valuation && (
                <div className="koncern-valuation">
                  <h2 className="koncern-val-title">Koncernvärdering</h2>
                  <p className="koncern-val-branch">
                    Bransch: <strong>{valuation.branch}</strong> &nbsp;·&nbsp;
                    Multipel: {valuation.multiple_low}× – {valuation.multiple_mid}× – {valuation.multiple_high}×
                  </p>
                  {valuation.warning && <p className="koncern-warning">{valuation.warning}</p>}

                  <div className="koncern-val-grid">
                    <div className="koncern-val-card">
                      <div className="koncern-val-label">Koncern EBITDA</div>
                      <div className="koncern-val-value">{fmt(valuation.group_ebitda)}</div>
                    </div>
                    <div className="koncern-val-card">
                      <div className="koncern-val-label">Koncern nettoskuld</div>
                      <div className="koncern-val-value">{fmt(valuation.group_net_debt)}</div>
                    </div>
                    <div className="koncern-val-card">
                      <div className="koncern-val-label">Koncern omsättning</div>
                      <div className="koncern-val-value">{fmt(valuation.group_revenue)}</div>
                    </div>
                  </div>

                  <table className="koncern-table" style={{ marginTop: 20 }}>
                    <thead>
                      <tr>
                        <th></th>
                        <th className="koncern-num">Lågt</th>
                        <th className="koncern-num">Mid</th>
                        <th className="koncern-num">Högt</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Enterprise Value (EV)</td>
                        <td className="koncern-num">{fmt(valuation.ev_low)}</td>
                        <td className="koncern-num"><strong>{fmt(valuation.ev_mid)}</strong></td>
                        <td className="koncern-num">{fmt(valuation.ev_high)}</td>
                      </tr>
                      <tr>
                        <td>Equity Value</td>
                        <td className="koncern-num">{fmt(valuation.equity_low)}</td>
                        <td className="koncern-num"><strong>{fmt(valuation.equity_mid)}</strong></td>
                        <td className="koncern-num">{fmt(valuation.equity_high)}</td>
                      </tr>
                    </tbody>
                  </table>

                  {valuation.missing_ebitda?.length > 0 && (
                    <p className="koncern-warning" style={{ marginTop: 12 }}>
                      Saknar EBITDA-data för: {valuation.missing_ebitda.join(", ")}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── INDIVIDUAL COMPANY ── */}
          {selected !== "overview" && selectedCompany && (
            <div className="koncern-company-view">
              <div className="koncern-company-head">
                <div>
                  <h1 className="koncern-title">{selectedCompany.name || selectedCompany.orgnr}</h1>
                  <p className="koncern-sub">
                    {selectedCompany.orgnr}
                    {selectedCompany.fiscal_year ? ` · Räkenskapsår ${selectedCompany.fiscal_year}` : ""}
                    {selectedCompany.isParent ? " · Moderbolag" : " · Dotterbolag"}
                  </p>
                </div>
                <button className="koncern-back-btn" onClick={() => setSelected("overview")}>
                  ← Koncernöversikt
                </button>
              </div>

              {/* Key metrics */}
              <div className="koncern-metrics-grid">
                <div className="koncern-metric-card">
                  <div className="koncern-val-label">Omsättning</div>
                  <div className="koncern-val-value">{fmt(selectedCompany.revenue)}</div>
                </div>
                <div className="koncern-metric-card">
                  <div className="koncern-val-label">EBITDA</div>
                  <div className="koncern-val-value">{fmt(selectedCompany.ebitda)}</div>
                </div>
                <div className="koncern-metric-card">
                  <div className="koncern-val-label">Nettoskuld</div>
                  <div className="koncern-val-value">{fmt(selectedCompany.net_debt)}</div>
                </div>
              </div>

              {/* Workspace navigation */}
              <div className="koncern-nav-section">
                <div className="koncern-nav-label">Öppna i plattformen</div>
                <div className="koncern-nav-links">
                  {COMPANY_LINKS.map(({ label, path }) => (
                    <Link key={label} to={path(selectedCompany.orgnr)} className="koncern-nav-link">
                      {label} →
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selected !== "overview" && !selectedCompany && !loading && (
            <div className="koncern-empty" style={{ padding: 32 }}>Bolaget hittades inte i koncernen.</div>
          )}
        </div>
      </div>
    </Main>
  );
}
